
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDarkMode } from '../../App';

export default function Landing() {
  const [isEntering, setIsEntering] = useState(false);
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const [menuOpen, setMenuOpen] = useState(false);
  const [portfolioOpen, setPortfolioOpen] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [showLogo, setShowLogo] = useState(false);

  useEffect(() => {
    // Show logo after 1 second
    const logoTimer = setTimeout(() => {
      setShowLogo(true);
    }, 1000);

    // Show video after 2 seconds (logo animation time)
    const videoTimer = setTimeout(() => {
      setShowVideo(true);
    }, 2000);

    return () => {
      clearTimeout(logoTimer);
      clearTimeout(videoTimer);
    };
  }, []);

  const handleEnter = () => {
    setIsEntering(true);
    setTimeout(() => {
      window.REACT_APP_NAVIGATE('/home');
    }, 800);
  };

  return (
    <div className={`fixed inset-0 ${isDarkMode ? 'bg-[#1a1a1a]' : 'bg-white'} flex items-center justify-center transition-all duration-800 ${isEntering ? 'opacity-0' : 'opacity-100'}`}>
      {/* Top Right Icons */}
      <div className="absolute top-4 right-4 md:top-8 md:right-8 flex items-center gap-3 md:gap-4 z-50">
        {/* Dark Mode Toggle */}
        <button
          onClick={toggleDarkMode}
          className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center ${isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'} transition-colors cursor-pointer`}
        >
          <i className={`ri-${isDarkMode ? 'moon' : 'sun'}-line text-lg md:text-xl`}></i>
        </button>

        {/* Menu Toggle */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center ${isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'} transition-colors cursor-pointer`}
        >
          <i className="ri-menu-line text-lg md:text-xl"></i>
        </button>
      </div>

      {/* Slide-out Menu */}
      <div className={`fixed top-0 right-0 h-full w-72 md:w-80 ${isDarkMode ? 'bg-[#0d0d0d]' : 'bg-[#F2F2F2]'} shadow-2xl transform transition-transform duration-500 z-40 ${menuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="p-6 md:p-8">
          {/* Close Button */}
          <button
            onClick={() => setMenuOpen(false)}
            className={`absolute top-6 right-6 md:top-8 md:right-8 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center ${isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'} transition-colors cursor-pointer`}
          >
            <i className="ri-close-line text-lg md:text-xl"></i>
          </button>

          {/* Menu Content */}
          <nav className="mt-16 md:mt-20 space-y-5 md:space-y-6">
            <Link 
              to="/home" 
              onClick={() => setMenuOpen(false)}
              className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/90 hover:text-white' : 'text-[#000000]/90 hover:text-[#000000]'} transition-colors`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Home
            </Link>
            <a 
              href="https://order.cinestate.ca/booking/new"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMenuOpen(false)}
              className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/90 hover:text-white' : 'text-[#000000]/90 hover:text-[#000000]'} transition-colors`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Listing Packages
            </a>
            <Link 
              to="/packages" 
              onClick={() => setMenuOpen(false)}
              className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/90 hover:text-white' : 'text-[#000000]/90 hover:text-[#000000]'} transition-colors`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Content Packages
            </Link>
            <Link 
              to="/staging" 
              onClick={() => setMenuOpen(false)}
              className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/90 hover:text-white' : 'text-[#000000]/90 hover:text-[#000000]'} transition-colors`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Home Staging
            </Link>
            {/* Portfolio Dropdown */}
            <div>
              <button
                onClick={() => setPortfolioOpen(!portfolioOpen)}
                className={`flex items-center justify-between w-full text-sm md:text-base font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/90 hover:text-white' : 'text-[#000000]/90 hover:text-[#000000]'} transition-colors cursor-pointer`}
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                <span>Portfolio</span>
                <i className={`${portfolioOpen ? 'ri-subtract-line' : 'ri-add-line'} text-xs`}></i>
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${portfolioOpen ? 'max-h-24 mt-3' : 'max-h-0'}`}>
                <div className="ml-4 space-y-3">
                  <Link
                    to="/portfolio"
                    onClick={() => setMenuOpen(false)}
                    className={`block text-xs md:text-sm font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/55 hover:text-white' : 'text-[#000000]/55 hover:text-[#000000]'} transition-colors`}
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    Residential
                  </Link>
                  <Link
                    to="/portfolio/commercial"
                    onClick={() => setMenuOpen(false)}
                    className={`block text-xs md:text-sm font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/55 hover:text-white' : 'text-[#000000]/55 hover:text-[#000000]'} transition-colors`}
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    Commercial
                  </Link>
                </div>
              </div>
            </div>
            <a 
              href="/home#contact" 
              onClick={() => setMenuOpen(false)}
              className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/90 hover:text-white' : 'text-[#000000]/90 hover:text-[#000000]'} transition-colors`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Contact
            </a>
          </nav>

          {/* Social Links */}
          <div className="absolute bottom-8 md:bottom-12 left-6 md:left-8 flex gap-3 md:gap-4">
            <a href="https://www.instagram.com/kianikanstudio?igsh=M3RoZ25wc25oZHY0" target="_blank" rel="noopener noreferrer" className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border ${isDarkMode ? 'border-white/20 text-white/70 hover:border-white hover:text-white' : 'border-[#000000]/20 text-[#000000]/70 hover:border-[#000000] hover:text-[#000000]'} transition-colors`}>
              <i className="ri-instagram-line text-sm md:text-base"></i>
            </a>
            <a href="https://youtube.com/@kianikanstudio3017?si=QvudTCaWtHR5bmy8" target="_blank" rel="noopener noreferrer" className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border ${isDarkMode ? 'border-white/20 text-white/70 hover:border-white hover:text-white' : 'border-[#000000]/20 text-[#000000]/70 hover:border-[#000000] hover:text-[#000000]'} transition-colors`}>
              <i className="ri-youtube-line text-sm md:text-base"></i>
            </a>
            <a href="https://www.tiktok.com/@kianikanstudio.ca?_r=1&_t=ZS-91yzSCjzfEY" target="_blank" rel="noopener noreferrer" className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border ${isDarkMode ? 'border-white/20 text-white/70 hover:border-white hover:text-white' : 'border-[#000000]/20 text-[#000000]/70 hover:border-[#000000] hover:text-[#000000]'} transition-colors`}>
              <i className="ri-tiktok-line text-sm md:text-base"></i>
            </a>
            <a href="https://www.instagram.com/kianikanstudio?igsh=M3RoZ25wc25oZHY0" target="_blank" rel="noopener noreferrer" className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border ${isDarkMode ? 'border-white/20 text-white/70 hover:border-white hover:text-white' : 'border-[#000000]/20 text-[#000000]/70 hover:border-[#000000] hover:text-[#000000]'} transition-colors`}>
              <i className="ri-facebook-fill text-sm md:text-base"></i>
            </a>
          </div>
        </div>
      </div>

      {/* Overlay when menu is open */}
      {menuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30"
          onClick={() => setMenuOpen(false)}
        ></div>
      )}

      {/* Video Background - appears after 2 seconds, starts at 5 seconds */}
      {showVideo && (
        <div className="absolute inset-0 overflow-hidden">
          <iframe
            src="https://www.youtube.com/embed/I3xMyWMwWfc?autoplay=1&mute=1&loop=1&playlist=I3xMyWMwWfc&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&start=5"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
            style={{ 
              width: '100vw',
              height: '100vh',
              minWidth: '177.77vh',
              minHeight: '56.25vw'
            }}
            allow="autoplay; encrypted-media"
            allowFullScreen
          ></iframe>
          <div className={`absolute inset-0 ${isDarkMode ? 'bg-[#1a1a1a]/70' : 'bg-white/70'}`}></div>
        </div>
      )}

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center px-6 md:px-8 h-full">
        {/* Logo - appears after 1 second with fade-in animation */}
        <div className={`text-center transition-opacity duration-1000 ${showLogo ? 'opacity-100' : 'opacity-0'}`}>
          <img 
            src="https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/91365b59b7e8e66ad4ab2980732fafc0.png"
            alt="CINÉSTATE"
            className={`h-8 md:h-12 lg:h-14 w-auto ${isDarkMode ? 'brightness-0 invert' : ''}`}
          />
        </div>
      </div>

      {/* Enter Button - Bottom Center */}
      <div className="absolute bottom-12 md:bottom-16 left-1/2 transform -translate-x-1/2 z-10">
        <button
          onClick={handleEnter}
          className={`group relative px-5 py-2 md:px-6 md:py-2 border ${isDarkMode ? 'border-white/40 text-white hover:border-white' : 'border-[#000000]/40 text-[#000000] hover:border-[#000000]'} text-[10px] md:text-xs tracking-[0.2em] uppercase overflow-hidden transition-all duration-500 cursor-pointer whitespace-nowrap font-normal`}
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          <span className={`relative z-10 ${isDarkMode ? 'group-hover:text-[#000000]' : 'group-hover:text-white'} transition-colors duration-500`}>
            Enter Website
          </span>
          <div className={`absolute inset-0 ${isDarkMode ? 'bg-white' : 'bg-[#000000]'} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`}></div>
        </button>
      </div>
    </div>
  );
}
